<script context="module" lang="ts">
  import { createContext } from '$utils/context';
  import { derived, writable, type Writable } from 'svelte/store';

  export function isProfileEmpty(user: RetrieveProfileResponse) {
    return (
      user.extendedProfile &&
      (user.extendedProfile.jobRoles.length > 0 ||
        user.extendedProfile.rolesLookingFor.length > 0 ||
        user.extendedProfile.interestedUseCases.length > 0 ||
        user.extendedProfile.lookingToBuild.length > 0 ||
        user.extendedProfile.lookingForCollab ||
        user.extendedProfile.skills.length > 0 ||
        user.extendedProfile.about.length > 0)
    );
  }

  export const showEditingProfileContext = createContext<Writable<boolean>>(
    'showEditingProfile',
    writable(false),
  );
</script>

<script lang="ts">
  import Button from '$components/reusable/Button.svelte';
  import CornerIcon from '$icons/CornerIcon.svelte';
  import GlobeIcon from '$icons/GlobeIcon.svelte';
  import LuggageIcon from '$icons/LuggageIcon.svelte';
  import PinIcon from '$icons/PinIcon.svelte';
  import GithubLogo from '$icons/logos/GithubLogo.svelte';
  import TelegramLogo from '$icons/logos/TelegramLogo.svelte';
  import XLogo from '$icons/logos/XLogo.svelte';
  import EditPencilIcon from '$icons/edit/EditPencilIcon.svelte';
  import { page } from '$app/stores';
  import MessageBubbleIcon from '$icons/MessageBubbleIcon.svelte';
  import MessageModal from '$components/reusable/messages/MessageModal.svelte';
  import type { LayoutData } from './$types';
  import ImageRender from '$components/reusable/images/ImageRender.svelte';
  import LinkedInLogo from '$icons/logos/LinkedInLogo.svelte';
  import Tabs from '$components/reusable/Tabs.svelte';
  import { browser } from '$app/environment';
  import { goto } from '$app/navigation';
  import ProfileBadges from '$lib/components/reusable/ProfileBadges.svelte';
  import EditProfile from '$lib/pages/MyProfile/Edit/EditProfile.svelte';
  import type { RetrieveProfileCompletionResponse, RetrieveProfileResponse } from '$shared/api';
  import ProfileIncompleteNotification from '$lib/pages/MyProfile/ProfileIncompleteNotification.svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { client } from '$lib/api/Client';
  import ProfileSkeleton from '$lib/components/reusable/profiles/ProfileSkeleton.svelte';

  export let data: LayoutData;

  let showMessageModal = false;
  const showEditingProfile = showEditingProfileContext.get();

  const profileQuery = createQuery(
    derived(page, ($page) => ({
      queryKey: ['profile', $page.params.username],
      queryFn: () => client.users.retrieveProfile({ username: $page.params.username }),
    })),
  );

  $: tabs = [
    { name: 'About', href: `/profiles/${$page.params.username}` },
    {
      name: 'Activity',
      href: `/profiles/${$page.params.username}/activity`,
    },
  ];
  $: ({ pathname } = $page.url);
  $: selectedTab = pathname.includes('activity') ? 1 : 0;
  $: isOwnProfile = $page.params.username === data.user?.username;
</script>

{#if $profileQuery.isLoading}
  <ProfileSkeleton />
{:else if $profileQuery.isError}
  <div class="flex h-[50vh] w-full items-center justify-center">
    <div class="text-center">
      <h2 class="text-gray-dark-12 mb-2 text-xl">Failed to load profile</h2>
      <p class="text-gray-dark-11">Please try again later</p>
    </div>
  </div>
{:else if $profileQuery.isSuccess}
  {#key $profileQuery.dataUpdatedAt}
    <EditProfile
      profile={$profileQuery.data.profile}
      extendedProfile={$profileQuery.data.extendedProfile}
      acceleratorProfile={$profileQuery.data.acceleratorProfile}
      bind:showEditProfileSlideover={$showEditingProfile}
    />
  {/key}

  <MessageModal bind:showMessageModal bind:receiverId={$profileQuery.data.profile.id} />

  {#if isOwnProfile && !data.user?.profileComplete}
    <ProfileIncompleteNotification />
  {/if}

  <main class="flex w-full justify-center">
    <div class="w-full">
      <div class="rounded-2xs md:border-gray-dark-6 md:p-3xs w-full border border-transparent p-0">
        <div class="rounded-2xs md:border-gray-dark-2 md:p-md w-full border border-transparent">
          <!-- Details -->
          <div
            class="gap-x-lg rounded-3xs border-gray-dark-1 bg-gray-dark-1 p-2xs relative flex w-full flex-col gap-y-3 border md:flex-row"
          >
            <CornerIcon class="text-gray-dark-12 absolute right-0 top-0 -m-0.5" />
            <CornerIcon class="text-gray-dark-12 absolute bottom-0 left-0 -m-0.5 rotate-180 " />
            <div class="flex justify-between">
              <div class="h-[88px] w-[88px]">
                {#if browser}
                  <ImageRender
                    src={$profileQuery.data.profile.avatarUrl ?? undefined}
                    seed={$profileQuery.data.profile.username}
                    class="h-[88px] w-[88px]"
                    imgClass="rounded"
                  />
                {/if}
                <!-- <Avatar src={user?.avatarUrl} seed={user?.username} class="h-[88px] w-[88px]" /> -->
              </div>
            </div>
            <div class="flex flex-1 flex-col items-start">
              <div class="flex items-center gap-2">
                <h2 class="font-architekt text-gray-dark-12 text-2xl font-normal">
                  {$profileQuery.data.profile.displayName}
                </h2>
                <ProfileBadges
                  roles={$profileQuery.data.profile.accountRoles}
                  batches={$profileQuery.data.profile.batches}
                />
              </div>
              <div class="text-gray-dark-11 mb-2 mt-1 flex items-center gap-x-2 text-sm leading-4">
                <span>@{$profileQuery.data.profile.username}</span>
              </div>
            </div>
          </div>
          <div
            class="mt-md gap-y-md text-gray-dark-11 flex flex-wrap items-center gap-x-4 text-xs leading-4"
          >
            {#if $profileQuery.data.extendedProfile.currentPosition}
              <div class="flex items-center gap-x-2">
                <LuggageIcon />
                <span>{$profileQuery.data.extendedProfile.currentPosition}</span>
              </div>
            {/if}
            {#if $profileQuery.data.extendedProfile.city || $profileQuery.data.extendedProfile.country}
              <div class="flex items-center gap-x-2">
                <PinIcon />
                <span
                  >{$profileQuery.data.extendedProfile.city ?? ''}, {$profileQuery.data
                    .extendedProfile.country ?? ''}</span
                >
              </div>
            {/if}
            {#if $profileQuery.data.extendedProfile.languages?.length}
              <div class="flex items-center gap-x-2">
                <GlobeIcon />
                <span>{$profileQuery.data.extendedProfile.languages.join(', ')}</span>
              </div>
            {/if}
          </div>
          <!-- Socials -->
          <div class="mt-md gap-xl flex flex-col items-center justify-between md:flex-row-reverse">
            {#if $page.params.username == $page.data.username}
              <Button
                secondary
                size="md"
                class="w-full md:w-fit"
                on:click={() => ($showEditingProfile = !$showEditingProfile)}
              >
                <EditPencilIcon slot="left-icon" />
                <span>Edit Profile</span>
              </Button>
            {:else}
              <Button
                secondary
                size="md"
                class="mt-md w-full md:w-fit"
                on:click={() =>
                  $page.data.username ? (showMessageModal = !showMessageModal) : goto('/signup')}
              >
                <MessageBubbleIcon slot="left-icon" />
                <span>Message</span>
              </Button>
            {/if}
            <div class="flex items-center gap-x-2">
              {#if $profileQuery.data.extendedProfile.telegramHandle}
                <Button
                  class="mt-md"
                  target="_blank"
                  href={`https://t.me/${$profileQuery.data.extendedProfile.telegramHandle}`}
                  quinary
                  size="sm"
                  icon><TelegramLogo /></Button
                >
              {/if}
              {#if $profileQuery.data.extendedProfile.githubHandle}
                <Button
                  class="mt-md"
                  target="_blank"
                  href={`https://github.com/${$profileQuery.data.extendedProfile.githubHandle}`}
                  quinary
                  size="sm"
                  icon><GithubLogo /></Button
                >
              {/if}
              {#if $profileQuery.data.extendedProfile.twitterHandle}
                <Button
                  class="mt-md"
                  target="_blank"
                  href={`https://twitter.com/${$profileQuery.data.extendedProfile.twitterHandle}`}
                  quinary
                  size="sm"
                  icon><XLogo /></Button
                >
              {/if}
              {#if $profileQuery.data.extendedProfile.linkedinHandle}
                <Button
                  class="mt-md"
                  target="_blank"
                  href={$profileQuery.data.extendedProfile.linkedinHandle}
                  quinary
                  size="sm"
                  icon><LinkedInLogo /></Button
                >
              {/if}
            </div>
          </div>
        </div>
      </div>

      <!-- OPEN TO BUILD -->
      <div class="my-6 w-full">
        <Tabs bind:tabs bind:selectedTab stretch class="!w-full" />
      </div>
      <div>
        <slot />
      </div>
    </div>
  </main>
{/if}
